/**
 * `PackageTypeForm` — create/edit form for a single package-type facade
 * (bet / frame / principle). A thin wrapper over the unified package columns
 * that locks `package_type` to the facade's type (the type selector is replaced
 * by a read-only chip).
 *
 * Because bets/frames/principles are *not* separate API resources, the form
 * binds the Refine `useForm` to the real `packages` resource (so the row lands
 * in `/api/packages` with the right type) while the route lives at the facade's
 * path (`/bets/create`, `/bets/:id/edit`). On submit/cancel it navigates back to
 * the facade list via literal paths. Validated against `createPackageSchema`.
 */
import { useEffect } from "react";
import { useForm } from "@refinedev/react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useGo } from "@refinedev/core";
import { useParams } from "react-router-dom";

import {
  TARGET_LEVELS,
  PACKAGE_MATURITY_STAGES,
  createPackageSchema,
  type CreatePackageInput,
  type PackageType,
} from "@aos/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/layout/PageHeader";
import { MarkdownEditor } from "@/components/app/MarkdownEditor";

import {
  NumberField,
  optionsFromEnum,
  SelectField,
  TextAreaField,
  TextField,
} from "@/resources/skills/_components/form";
import { humanize } from "@/resources/projects/_form";

export interface PackageTypeFormProps {
  action: "create" | "edit";
  /** Route base (facade path), e.g. "bets". */
  resource: string;
  /** The locked `package_type` for this facade. */
  packageType: PackageType;
  /** Human label, e.g. "Bet". */
  label: string;
}

export function PackageTypeForm({
  action,
  resource,
  packageType,
  label,
}: PackageTypeFormProps) {
  const go = useGo();
  const { id } = useParams<{ id: string }>();

  const form = useForm<CreatePackageInput>({
    resolver: zodResolver(createPackageSchema),
    // Bind to the real `packages` resource; `redirect:false` so we route to the
    // facade list ourselves (the facade path is not a Refine resource).
    refineCoreProps: {
      resource: "packages",
      action,
      id: action === "edit" ? id : undefined,
      redirect: false,
      onMutationSuccess: () => go({ to: `/${resource}` }),
    },
  });

  const {
    refineCore: { onFinish, formLoading },
    handleSubmit,
    watch,
    setValue,
  } = form;

  // Lock the package_type to this facade's type (create defaults it; edit keeps
  // it). The selector is replaced by a read-only chip below.
  useEffect(() => {
    setValue("package_type", packageType, { shouldValidate: true });
  }, [packageType, setValue]);

  const body = watch("body_markdown") ?? "";

  return (
    <form
      onSubmit={handleSubmit((values) =>
        onFinish({ ...values, package_type: packageType }),
      )}
      className="mx-auto max-w-2xl space-y-6"
    >
      <PageHeader
        title={action === "create" ? `New ${label}` : `Edit ${label}`}
        subtitle={`${
          action === "create" ? "Add a" : "Update this"
        } ${label.toLowerCase()} in the unified package store.`}
      />
      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="space-y-1.5">
            <Label>Package type</Label>
            <div>
              <Badge variant="outline">{humanize(packageType)}</Badge>
            </div>
          </div>

          <TextField
            form={form}
            name="name"
            label="Name"
            placeholder={`${label} name`}
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <SelectField
              form={form}
              name="target_level"
              label="Target Level"
              options={optionsFromEnum(TARGET_LEVELS)}
            />
            <SelectField
              form={form}
              name="maturity_stage"
              label="Maturity Stage"
              options={optionsFromEnum(PACKAGE_MATURITY_STAGES)}
              allowEmpty
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              form={form}
              name="status"
              label="Status"
              placeholder="draft"
            />
            <NumberField
              form={form}
              name="version"
              label="Version"
              placeholder="1"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              form={form}
              name="domain"
              label="Domain"
              placeholder="e.g. operations"
            />
            <TextField
              form={form}
              name="department"
              label="Department"
              placeholder="e.g. estimating"
            />
          </div>

          <TextField
            form={form}
            name="owner_user_id"
            label="Owner User ID"
            placeholder="user_…"
          />

          <TextAreaField
            form={form}
            name="description"
            label="Description"
            placeholder={`What this ${label.toLowerCase()} represents`}
          />

          <div className="space-y-1.5">
            <Label htmlFor="body_markdown">Body (Markdown)</Label>
            <MarkdownEditor
              value={body}
              onChange={(next) =>
                setValue("body_markdown", next, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
            />
          </div>
        </CardContent>
        <CardFooter className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => go({ to: `/${resource}` })}
            disabled={formLoading}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={formLoading}>
            {formLoading ? "Saving…" : "Save"}
          </Button>
        </CardFooter>
      </Card>
    </form>
  );
}

export default PackageTypeForm;
